import { Component, Input, OnInit, Optional } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  UntypedFormArray,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { IPaymentsList } from 'src/app/@interfaces/payments.interface';
import { PortfolioService } from 'src/app/@pages/portfolio/portfolio.service';
import Swal from 'sweetalert2';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { UsersService } from '../../../../../@pages/users/users.service';
import { IUser } from 'src/app/@interfaces/user.interface';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { VOneIconComponent } from '@shared-components';

@Component({
  selector: 'app-crud-banks',
  templateUrl: './crud-banks.component.html',
  styleUrls: ['./crud-banks.component.css'],
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    FormsModule,
    VOneIconComponent,
    TranslatePipe,
  ],
})
export class CrudBanksComponent implements OnInit {
  registerForm: UntypedFormGroup = this.fb.group({
    itemRows: this.fb.array([this.createRow()]),
  });

  get rowsArr(): UntypedFormArray {
    return this.registerForm.get('itemRows') as UntypedFormArray;
  }

  @Input() listBanks: Array<IPaymentsList> = [];
  @Input() groupPayment: string = '';
  @Input() showOwnHeader: boolean = true;
  @Input() showOwnActions: boolean = true;
  userList: Array<IUser> = [];
  unsubscribe$ = new Subject<void>();
  constructor(
    private user: UsersService,
    private fb: UntypedFormBuilder,
    private portfolio: PortfolioService,
    private router: Router,
    private translate: TranslateService,
    @Optional() private dialogRef: MatDialogRef<unknown>,
  ) {}

  ngOnInit(): void {
    this.portfolio
      .getBankList()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.listBanks = result;
      });
    this.user
      .listUsers()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result: any) => {
        this.userList = result;
      });
  }

  private createRow(): UntypedFormGroup {
    return this.fb.group({
      bank: ['', [Validators.required]],
      user: ['', [Validators.required]],
    });
  }

  addRow(): void {
    this.rowsArr.push(this.createRow());
  }

  private resetRows(): void {
    while (this.rowsArr.length > 1) {
      this.rowsArr.removeAt(this.rowsArr.length - 1);
    }
    this.rowsArr.at(0).reset();
  }

  remove(id: number, index: number) {
    Swal.fire({
      title: this.translate.instant('Are you sure?'),
      text: this.translate.instant('You won not be able to revert this!'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff5400',
      cancelButtonColor: 'rgb(0, 0, 0)',
      confirmButtonText: 'Confirm',
    }).then((confirm) => {
      if (confirm.isConfirmed) {
        this.portfolio
          .removePayments(id)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((result) => {
            if (!result) {
              Swal.fire(
                'Error',
                this.translate.instant('Something was wrong'),
                'error',
              );
            } else {
              Swal.fire(
                this.translate.instant('Good'),
                this.translate.instant('Bank removed'),
                'success',
              ).then((data) => {
                if (data.isConfirmed) {
                  if (this.groupPayment === 'Portfolio') {
                    this.router
                      .navigateByUrl('/dialog', { skipLocationChange: true })
                      .then(() => {
                        this.router.navigate(['/portfolio']);
                      });
                  } else {
                    this.router
                      .navigateByUrl('/dialog', { skipLocationChange: true })
                      .then(() => {
                        this.router.navigate(['/accountsPayable']);
                      });
                  }
                  this.portfolio
                    .getBankList()
                    .pipe(takeUntil(this.unsubscribe$))
                    .subscribe((result) => {
                      this.listBanks = result;
                    });
                }
              });
            }
          });
      }
    });
  }

  async update(): Promise<void> {
    if (this.registerForm.invalid === true) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: this.translate.instant('Bank Invalid, Please Check!'),
      });
      return;
    }

    const rows = this.rowsArr.value as { bank: string; user: IUser }[];

    try {
      for (const row of rows) {
        await firstValueFrom(
          this.portfolio.createPayments(
            'Bank',
            row.bank,
            parseInt(row.user.id.toString()),
          ),
        );
      }
    } catch {
      Swal.fire(
        this.translate.instant('Something was wrong'),
        this.translate.instant('Failed'),
        'error',
      );
      return;
    }

    Swal.fire({
      title: this.translate.instant('Created'),
      text: this.translate.instant('Bank created successfully!'),
      icon: 'success',
      showCancelButton: false,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Ok',
    }).then((data) => {
      if (data.isConfirmed) {
        if (this.groupPayment === 'Portfolio') {
          this.router
            .navigateByUrl('/dialog', { skipLocationChange: true })
            .then(() => {
              this.router.navigate(['/portfolio']);
            });
        } else {
          this.router
            .navigateByUrl('/dialog', { skipLocationChange: true })
            .then(() => {
              this.router.navigate(['/accountsPayable']);
            });
        }
        this.portfolio
          .getBankList()
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((result) => {
            this.listBanks = result;
          });
        this.resetRows();
        if (!this.showOwnActions) {
          this.dialogRef?.close();
        }
      }
    });
  }

  textValid(text: string, index: number) {
    const control = this.rowsArr.at(index)?.get(text);
    return control?.invalid && control?.touched;
  }

  getUser(list: IUser[], id: number): string {
    let name = '';
    list.forEach((element) => {
      if (parseInt(element.id.toString()) === parseInt(id.toString())) {
        name = element.name;
      }
    });
    return name;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
